/** Configuração de ambiente do app (endereços dos serviços). Nada aqui é segredo: vai para o navegador. */
export interface PlatformEnv {
  /** Raiz do Gateway com `/api` (ex.: `http://localhost:5000/api`). */
  apiBase: string;
  /** WebSocket do Match pelo Gateway. */
  wsUrl: string;
  /** Raiz dos assets publicados (`manifest.json` e `objects/`). */
  assetsUrl: string;
}

/** Armazenamento chave-valor síncrono e pequeno (sessão, preferências). No desktop vai para o cofre do sistema. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type PlatformKind = 'web' | 'desktop' | 'mobile';

/** O que o app precisa do ambiente. Cada plataforma (web aqui; Electron na 6.2) fornece uma implementação. */
export interface Platform {
  readonly kind: PlatformKind;
  readonly env: PlatformEnv;
  readonly storage: KeyValueStorage;
  /** Abre um link no navegador do sistema (no web, numa aba nova). Só http e https. */
  openExternal(url: string): Promise<void>;
  /** Pede uma notificação do sistema (convite, fim de partida). Devolve falso se não há permissão ou suporte. */
  notify(title: string, body: string): Promise<boolean>;
}

/** Memória: para testes e para quando o navegador bloqueia o `localStorage` (modo privado, política). */
export class MemoryStorage implements KeyValueStorage {
  private readonly data = new Map<string, string>();

  getItem(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }
}

/** `localStorage` que nunca lança: se estiver indisponível, cai para memória (a sessão vale só até recarregar). */
export function safeBrowserStorage(storage?: Storage): KeyValueStorage {
  const memory = new MemoryStorage();
  let target: Storage | null = null;
  try {
    target = storage ?? globalThis.localStorage ?? null;
    target?.getItem('__ddt_probe__');
  } catch {
    target = null;
  }
  return {
    getItem(key) {
      try {
        return target ? target.getItem(key) : memory.getItem(key);
      } catch {
        return memory.getItem(key);
      }
    },
    setItem(key, value) {
      try {
        if (target) target.setItem(key, value);
        else memory.setItem(key, value);
      } catch {
        memory.setItem(key, value);
      }
    },
    removeItem(key) {
      try {
        target?.removeItem(key);
      } catch {
        // ignora
      }
      memory.removeItem(key);
    },
  };
}

/** Guarda um objeto JSON em uma chave, com leitura tolerante a lixo (valor corrompido vira `null`). */
export function jsonStore<T>(
  storage: KeyValueStorage,
  key: string,
  validate: (value: unknown) => value is T,
): { get(): T | null; set(value: T | null): void } {
  return {
    get() {
      const raw = storage.getItem(key);
      if (raw === null) return null;
      try {
        const parsed: unknown = JSON.parse(raw);
        return validate(parsed) ? parsed : null;
      } catch {
        return null;
      }
    },
    set(value) {
      if (value === null) storage.removeItem(key);
      else storage.setItem(key, JSON.stringify(value));
    },
  };
}

export function isSafeExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export interface WebPlatformOptions {
  env: PlatformEnv;
  storage?: KeyValueStorage;
  /** Para testes. */
  open?: (url: string) => void;
}

export function createWebPlatform(options: WebPlatformOptions): Platform {
  return {
    kind: 'web',
    env: options.env,
    storage: options.storage ?? safeBrowserStorage(),
    openExternal(url) {
      if (!isSafeExternalUrl(url)) return Promise.reject(new Error('Só links http e https podem ser abertos.'));
      (options.open ?? ((u) => void window.open(u, '_blank', 'noopener,noreferrer')))(url);
      return Promise.resolve();
    },
    async notify(title, body) {
      if (typeof Notification === 'undefined') return false;
      if (Notification.permission === 'default') await Notification.requestPermission();
      if (Notification.permission !== 'granted') return false;
      new Notification(title, { body });
      return true;
    },
  };
}

/**
 * O que a preload do Electron expõe em `window.ddtDesktop` (6.2). É a única superfície entre a página e o processo principal:
 * armazenamento síncrono (cifrado pelo sistema), links externos e notificações. Cada chamada vira IPC validado no processo principal.
 */
export interface DesktopBridge {
  readonly env: PlatformEnv;
  readonly storage: KeyValueStorage;
  openExternal(url: string): Promise<void>;
  notify(title: string, body: string): Promise<boolean>;
}

declare global {
  interface Window {
    /** Só existe dentro do Electron. */
    ddtDesktop?: DesktopBridge;
  }
}

/** Lê a ponte do desktop se a página roda no Electron; `null` no navegador. */
export function desktopBridge(scope: { ddtDesktop?: DesktopBridge } = globalThis as { ddtDesktop?: DesktopBridge }): DesktopBridge | null {
  return scope.ddtDesktop ?? null;
}

export function createDesktopPlatform(bridge: DesktopBridge): Platform {
  return {
    kind: 'desktop',
    env: bridge.env,
    storage: bridge.storage,
    openExternal(url) {
      // Valida aqui também, para falhar rápido; o processo principal valida de novo (não confia na página).
      if (!isSafeExternalUrl(url)) return Promise.reject(new Error('Só links http e https podem ser abertos.'));
      return bridge.openExternal(url);
    },
    notify: (title, body) => bridge.notify(title, body),
  };
}
